import React, { ReactElement, useEffect } from 'react';
import { isSome, Option } from 'fp-ts/lib/Option';
import { FullScreen, FullScreenHandle, useFullScreenHandle } from 'react-full-screen';

import { ErrorData } from 'domain/types';
import useAutorefreshDashboard from 'adapter/in/dashboards/hooks/Dashboard/useAutorefreshDashboard';
import { useReadDashboard } from 'adapter/in/dashboards/hooks/Dashboard/useReadDashboard';
import { useReadWidgets } from 'adapter/in/dashboards/hooks/Widgets/useReadWidgets';
import { DashboardResult } from 'adapter/in/dashboards/context/Dashboard';
import { dispatchWidgetsView } from 'adapter/in/dashboards/context/Widgets/view';
import DashboardPage from 'adapter/in/dashboards/pages/Dashboard';
import styled from 'styled-components';
import { useAlertToaster } from 'adapter/in/common/context/Alert';
import { StyledAlertType } from 'adapter/in/common/components/Home/Home';

type DashboardContainerProps = {
    id: string;
};

type UseDashboardContainer = {
    hasData: boolean;
    data: DashboardResult;
    error: Option<ErrorData>;
    handleFullscreen: FullScreenHandle;
};

export const useDashboardContainer = (id: string): UseDashboardContainer => {
    const { data, error, reset: readDashboardReset } = useReadDashboard(id);
    const { data: readWidgetsResult, error: readWidgetsError, reset: readWidgetsReset } = useReadWidgets(id);
    const { setWidgets } = dispatchWidgetsView();

    useAutorefreshDashboard();

    useEffect(() => {
        if (isSome(data) && isSome(readWidgetsResult) && readWidgetsResult.value.length > 0) {
            setWidgets(readWidgetsResult.value);
        }
    }, [data, readWidgetsResult]);

    const onUnmountResetState = () => {
        readDashboardReset();
        readWidgetsReset();
    };

    useEffect(() => onUnmountResetState, []);

    return {
        hasData: isSome(readWidgetsResult) && isSome(data),
        data,
        error: isSome(error) ? error : readWidgetsError,
        handleFullscreen: useFullScreenHandle(),
    };
};

export const StyledFullScreen = styled(FullScreen)`
    height: 100%;
`;

export const DashboardContainer = (props: DashboardContainerProps): ReactElement => {
    const { id } = props;
    const { data, hasData, error, handleFullscreen } = useDashboardContainer(id);
    const { message: alertMessage, show: showAlert, type: alertType, style } = useAlertToaster();

    return (
        <StyledFullScreen handle={handleFullscreen}>
            <DashboardPage
                data={data}
                isLoading={!hasData}
                error={error}
                onFullscreen={handleFullscreen.enter}
                fullscreenActive={handleFullscreen.active}
                alertType={alertType as StyledAlertType}
                alertMessage={alertMessage}
                animationStyle={style}
                showAlert={showAlert}
            />
        </StyledFullScreen>
    );
};
